import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useAppContext } from '../context/AppContext';
import { isTodayDeliveryChoosable } from '../utils/mskTodayDelivery';

const CITIES = ['Казань', 'Зеленодольск', 'Волжск'];
const DISTRICTS = ['Мирный', 'Город', 'Зеленый дол'];

const buildAddress = (f) => [f.street, f.house ? `д. ${f.house}` : null, f.entrance ? `пд. ${f.entrance}` : null, f.floor ? `эт. ${f.floor}` : null, f.apartment ? `кв. ${f.apartment}` : null].filter(Boolean).join(', ');

const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const tomorrowISO = () => { const d = new Date(); d.setDate(d.getDate()+1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const fmtDate = (s) => { const [,m,d] = s.split('-'); return `${d}.${m}`; };

export default function OrderScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, minOrderQty: ctxMin, getCatalogUnitPrice } = useAppContext();
  const product = location.state?.product;
  const unitPrice = product ? getCatalogUnitPrice(product) : 0;

  const [quantity, setQuantity] = useState(ctxMin);
  const [addrFields, setAddrFields] = useState({ street: '', house: '', entrance: '', floor: '', apartment: '' });
  const [phone, setPhone] = useState(user?.phone || '');
  const [comment, setComment] = useState('');
  const [exchangeQty, setExchangeQty] = useState('');
  const [deliveryDay, setDeliveryDay] = useState('today');
  const [city, setCity] = useState(null);
  const [district, setDistrict] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [todayEnabled, setTodayEnabled] = useState(true);
  const [minOrderQty, setMinOrderQty] = useState(ctxMin);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addrPickerVisible, setAddrPickerVisible] = useState(false);
  const [clockTick, setClockTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setClockTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const exchangeCount = Number(exchangeQty || 0);
  const discountTotal = exchangeCount * 280;
  const finalTotal = product ? Math.max(0, unitPrice * quantity - discountTotal) : 0;

  const canChooseToday = useMemo(
    () => isTodayDeliveryChoosable(todayEnabled, 18, new Date()),
    [todayEnabled, clockTick],
  );
  useEffect(() => {
    if (deliveryDay === 'today' && !isTodayDeliveryChoosable(todayEnabled)) setDeliveryDay('tomorrow');
  }, [deliveryDay, todayEnabled, clockTick]);

  useEffect(() => {
    api.settings.get().then(s => {
      if (s?.today_delivery_enabled === false) { setTodayEnabled(false); setDeliveryDay('tomorrow'); }
      if (s?.min_order_quantity) { setMinOrderQty(s.min_order_quantity); setQuantity(q => Math.max(q, s.min_order_quantity)); }
    }).catch(() => {});
    const raw = localStorage.getItem(`saved_addresses_${user?.id}`);
    if (raw) setSavedAddresses(JSON.parse(raw));
    const saved = localStorage.getItem('order_form');
    if (saved) {
      try {
        const s = JSON.parse(saved);
        setPhone(s.phone || '');
        setAddrFields({ street: s.street || '', house: s.house || '', entrance: s.entrance || '', floor: s.floor || '', apartment: s.apartment || '' });
        if (s.quantity) setQuantity(s.quantity);
      } catch {}
    }
  }, [user]);

  const saveForm = (af, ph, q) => localStorage.setItem('order_form', JSON.stringify({ ...af, phone: ph, quantity: q }));

  const handlePhoneChange = (v) => {
    let c = v.replace(/[^\d+]/g, '');
    if (c.startsWith('8')) c = '+7' + c.slice(1);
    if (c.startsWith('7') && !c.startsWith('+7')) c = '+' + c;
    if (c.length > 12) return;
    setPhone(c);
    if (errors.phone) setErrors(p => ({ ...p, phone: null }));
  };

  const changeQty = (d) => {
    const min = user?.isAdmin ? 1 : minOrderQty;
    const n = Math.max(min, quantity + d);
    setQuantity(n);
    if (exchangeQty !== '' && Number(exchangeQty) > n) setExchangeQty(String(n));
    saveForm(addrFields, phone, n);
  };

  const validate = () => {
    const e = {};
    if (!user?.isAdmin && quantity < minOrderQty) e.quantity = `Минимальный заказ — ${minOrderQty} шт.`;
    if (!addrFields.street.trim()) e.address = 'Введите улицу';
    else if (!addrFields.house.trim()) e.address = 'Введите номер дома';
    if (!phone.trim()) e.phone = 'Введите телефон';
    else if (!/^\+7\d{10}$/.test(phone)) e.phone = 'Формат: +79991234567';
    if (!city) e.city = 'Выберите город доставки';
    if (city === 'Зеленодольск' && !district) e.district = 'Выберите район';
    if (exchangeQty === '') e.exchangeQty = 'Укажите бутыли на обмен (0 если нет)';
    else if (Number(exchangeQty) > quantity) e.exchangeQty = 'Не может быть больше количества';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.orders.create({
        brand: product.brand, volume: product.volume, price: unitPrice,
        address: buildAddress(addrFields), phone, quantity,
        exchange_count: exchangeCount, total_price: finalTotal,
        delivery_day: deliveryDay === 'today' && isTodayDeliveryChoosable(todayEnabled) ? todayISO() : tomorrowISO(),
        city, district: district || null, comment: comment.trim() || null,
      });
      localStorage.removeItem('order_form');
      setSuccess(true);
    } catch { alert('Не удалось оформить заказ. Попробуйте ещё раз.'); }
    finally { setLoading(false); }
  };

  if (!product) return <div style={{ padding: 24 }}><button onClick={() => navigate('/catalog')} style={backBtn}>← В каталог</button><p>Товар не выбран</p></div>;

  if (success) return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 16, textAlign: 'center' }}>
      <div style={{ fontSize: 64 }}>🎉</div>
      <h2 style={{ color: 'var(--primary)', fontSize: 22 }}>Заказ оформлен!</h2>
      <p style={{ color: 'var(--text-light)' }}>{product.brand} {product.volume} × {quantity}<br />Сумма: {finalTotal} ₽</p>
      <button onClick={() => navigate('/profile')} style={btnStyle}>Мои заказы</button>
      <button onClick={() => navigate('/catalog')} style={{ ...btnStyle, background: 'var(--input-bg)', color: 'var(--text)' }}>В каталог</button>
    </div>
  );

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <button onClick={() => navigate(-1)} style={backBtn}>← Назад</button>
      <h1 style={{ fontSize: 28, fontWeight: 'bold', color: 'var(--primary)', marginBottom: 16 }}>Оформление заказа</h1>

      {/* Карточка товара */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <span style={{ fontSize: 36 }}>{product.emoji || '💧'}</span>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 16 }}>{product.brand}</div>
          <div style={{ fontSize: 13, color: 'var(--text-light)' }}>{product.volume}</div>
          <div style={{ fontSize: 15, color: 'var(--text-light)' }}>{unitPrice} ₽/шт</div>
        </div>
      </div>

      <form onSubmit={handleOrder}>
        {/* Количество */}
        <label style={labelStyle}>Количество</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <button type="button" onClick={() => changeQty(-1)} style={qtyBtnStyle}>−</button>
          <input type="number" min={user?.isAdmin ? 1 : minOrderQty} max={999} value={quantity}
            onChange={e => { const n = parseInt(e.target.value); if (!isNaN(n) && n >= 1) { setQuantity(n); if (exchangeQty !== '' && Number(exchangeQty) > n) setExchangeQty(String(n)); } }}
            style={{ width: 52, height: 44, textAlign: 'center', fontSize: 20, fontWeight: 700, color: 'var(--text)', background: 'var(--input-bg)', border: '1.5px solid var(--border)', borderRadius: 10, outline: 'none' }} />
          <button type="button" onClick={() => changeQty(1)} style={qtyBtnStyle}>+</button>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)', marginLeft: 8 }}>= {finalTotal} ₽</span>
        </div>
        {errors.quantity && <p style={errorStyle}>⚠ {errors.quantity}</p>}
        {minOrderQty > 1 && <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 4 }}>Минимальный заказ: {minOrderQty} шт.</p>}

        {/* Обмен */}
        <label style={labelStyle}>Бутыли на обмен</label>
        <input type="number" min={0} placeholder="0" value={exchangeQty}
          onChange={e => { const v = e.target.value; if (Number(v) <= quantity) { setExchangeQty(v); setErrors(p => ({ ...p, exchangeQty: null })); } }}
          style={{ ...inputStyle, borderColor: errors.exchangeQty ? '#FF3B30' : 'var(--border)' }} />
        {errors.exchangeQty && <p style={errorStyle}>⚠ {errors.exchangeQty}</p>}
        <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 4 }}>Скидка: {discountTotal} ₽ (280 ₽ × {exchangeCount})</p>

        {/* Город */}
        <label style={labelStyle}>Город доставки</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          {CITIES.map(c => (
            <button key={c} type="button" onClick={() => { setCity(c); setDistrict(null); setErrors(p => ({ ...p, city: null })); }}
              style={{ flex: 1, padding: '10px 4px', borderRadius: 10, border: '1.5px solid', borderColor: city === c ? 'var(--primary)' : 'var(--border)', background: city === c ? 'var(--primary)' : 'var(--card)', color: city === c ? '#fff' : 'var(--text)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {c}
            </button>
          ))}
        </div>
        {errors.city && <p style={errorStyle}>⚠ {errors.city}</p>}

        {/* Район */}
        {city === 'Зеленодольск' && (
          <>
            <label style={labelStyle}>Район</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
              {DISTRICTS.map(d => (
                <button key={d} type="button" onClick={() => { setDistrict(d); setErrors(p => ({ ...p, district: null })); }}
                  style={{ flex: 1, padding: '10px 4px', borderRadius: 10, border: '1.5px solid', borderColor: district === d ? 'var(--primary)' : 'var(--border)', background: district === d ? 'var(--primary)' : 'var(--card)', color: district === d ? '#fff' : 'var(--text)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  {d}
                </button>
              ))}
            </div>
            {errors.district && <p style={errorStyle}>⚠ {errors.district}</p>}
          </>
        )}

        {/* Адрес */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={labelStyle}>Адрес доставки</label>
          {savedAddresses.length > 0 && (
            <button type="button" onClick={() => setAddrPickerVisible(true)} style={{ background: 'rgba(26,109,181,0.1)', border: '1px solid var(--primary)', borderRadius: 8, padding: '4px 10px', color: 'var(--primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Мои адреса
            </button>
          )}
        </div>
        <label style={addrLabelStyle}>Улица <span style={{ color: '#FF3B30' }}>*</span></label>
        <input placeholder="ул. Ленина" value={addrFields.street}
          onChange={e => { setAddrFields(p => ({ ...p, street: e.target.value })); setErrors(p => ({ ...p, address: null })); saveForm({ ...addrFields, street: e.target.value }, phone, quantity); }}
          style={{ ...inputStyle, borderColor: errors.address ? '#FF3B30' : 'var(--border)' }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={addrLabelStyle}>Дом <span style={{ color: '#FF3B30' }}>*</span></label>
            <input placeholder="1" value={addrFields.house}
              onChange={e => { setAddrFields(p => ({ ...p, house: e.target.value })); setErrors(p => ({ ...p, address: null })); }}
              style={{ ...inputStyle, borderColor: errors.address ? '#FF3B30' : 'var(--border)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={addrLabelStyle}>Подъезд</label>
            <input placeholder="2" value={addrFields.entrance} onChange={e => setAddrFields(p => ({ ...p, entrance: e.target.value }))} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={addrLabelStyle}>Этаж</label>
            <input placeholder="5" value={addrFields.floor} onChange={e => setAddrFields(p => ({ ...p, floor: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={addrLabelStyle}>Квартира</label>
            <input placeholder="10" value={addrFields.apartment} onChange={e => setAddrFields(p => ({ ...p, apartment: e.target.value }))} style={inputStyle} />
          </div>
        </div>
        {errors.address && <p style={errorStyle}>⚠ {errors.address}</p>}

        {/* Телефон */}
        <label style={labelStyle}>Телефон <span style={{ fontSize: 13, color: 'var(--text-light)', fontWeight: 400 }}>{phone.length}/12</span></label>
        <input type="tel" placeholder="+79991234567" value={phone} onChange={e => handlePhoneChange(e.target.value)}
          style={{ ...inputStyle, borderColor: errors.phone ? '#FF3B30' : 'var(--border)' }} />
        {errors.phone && <p style={errorStyle}>⚠ {errors.phone}</p>}

        {/* День доставки */}
        <label style={labelStyle}>День доставки</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          {todayEnabled && (
            <button
              type="button"
              disabled={!canChooseToday}
              title={!canChooseToday ? 'После 18:00 по Москве заказ на сегодня не принимается' : undefined}
              onClick={() => { if (canChooseToday) setDeliveryDay('today'); }}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 10,
                border: '1.5px solid',
                borderColor: deliveryDay === 'today' && canChooseToday ? 'var(--primary)' : 'var(--border)',
                background: deliveryDay === 'today' && canChooseToday ? 'var(--primary)' : 'var(--card)',
                color: deliveryDay === 'today' && canChooseToday ? '#fff' : 'var(--text)',
                fontWeight: 600,
                cursor: canChooseToday ? 'pointer' : 'not-allowed',
                opacity: canChooseToday ? 1 : 0.5,
              }}
            >
              Сегодня<br /><span style={{ fontSize: 12 }}>{fmtDate(todayISO())}</span>
            </button>
          )}
          <button type="button" onClick={() => setDeliveryDay('tomorrow')}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid', borderColor: deliveryDay === 'tomorrow' ? 'var(--primary)' : 'var(--border)', background: deliveryDay === 'tomorrow' ? 'var(--primary)' : 'var(--card)', color: deliveryDay === 'tomorrow' ? '#fff' : 'var(--text)', fontWeight: 600, cursor: 'pointer' }}>
            Завтра<br /><span style={{ fontSize: 12 }}>{fmtDate(tomorrowISO())}</span>
          </button>
        </div>

        {/* Комментарий */}
        <label style={labelStyle}>Комментарий <span style={{ fontSize: 13, color: 'var(--text-light)', fontWeight: 400 }}>{comment.length}/200</span></label>
        <textarea placeholder="Код домофона, этаж, пожелания..." value={comment}
          onChange={e => { if (e.target.value.length <= 200) setComment(e.target.value); }}
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />

        <button type="submit" disabled={loading} style={{ ...btnStyle, opacity: loading ? 0.6 : 1, marginTop: 16 }}>
          {loading ? 'Оформление...' : `Оформить заказ — ${finalTotal} ₽`}
        </button>
      </form>

      {/* Модальное окно выбора адреса */}
      {addrPickerVisible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 }} onClick={() => setAddrPickerVisible(false)}>
          <div style={{ background: 'var(--input-bg)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, width: '100%', maxHeight: '60vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 16 }}>Выберите адрес</h3>
            {savedAddresses.map(a => (
              <div key={a.id} style={{ background: 'var(--card)', borderRadius: 12, padding: 16, marginBottom: 8, cursor: 'pointer' }}
                onClick={() => { setAddrFields({ street: a.street || '', house: a.house || '', entrance: a.entrance || '', floor: a.floor || '', apartment: a.apartment || '' }); setCity(a.city); setDistrict(a.district || null); setErrors(p => ({ ...p, address: null, city: null, district: null })); setAddrPickerVisible(false); }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>{a.label}</div>
                <div style={{ fontSize: 14 }}>{buildAddress(a)}</div>
                <div style={{ fontSize: 13, color: 'var(--text-light)' }}>{a.city}{a.district ? ` · ${a.district}` : ''}</div>
              </div>
            ))}
            <button onClick={() => setAddrPickerVisible(false)} style={{ width: '100%', padding: 12, background: 'none', color: 'var(--text-light)', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}>Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = { background: 'var(--card)', borderRadius: 14, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };
const inputStyle = { display: 'block', width: '100%', background: 'var(--input-bg)', borderRadius: 12, padding: '12px 16px', fontSize: 15, color: 'var(--text)', border: '1.5px solid var(--border)', outline: 'none', marginBottom: 4 };
const labelStyle = { display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8, marginTop: 12 };
const addrLabelStyle = { display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginTop: 8, marginBottom: 6 };
const errorStyle = { fontSize: 13, color: '#FF3B30', marginBottom: 8 };
const btnStyle = { display: 'block', width: '100%', padding: 14, borderRadius: 14, background: 'var(--primary)', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' };
const backBtn = { background: 'none', color: 'var(--primary)', fontSize: 16, marginTop: 16, marginBottom: 16, cursor: 'pointer', padding: 0 };
const qtyBtnStyle = { width: 40, height: 40, borderRadius: 10, background: 'var(--primary)', color: '#fff', fontSize: 22, fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
